/**
 * El «cromo» de la página: índice lateral, revelado al entrar, encabezado sobre
 * secciones oscuras y respeto al movimiento reducido.
 *
 * Sale de `assets/home.js`, que en el prototipo es un solo archivo con también
 * el selector de color y el verificador de compatibilidad — esos dos ya viven
 * en `sib-product.js`, así que aquí va solo lo que faltaba.
 *
 * Se vuelve a enganchar con `shopify:section:load` para que funcione dentro del
 * editor de temas al recargar una sección.
 */

function initCromo() {
  /* ------------------------------------------------------- regla óptica ----
     Marca la sección activa según el scroll. Se usa IntersectionObserver con la
     ventana reducida a una banda central: así la sección "activa" es la que
     ocupa el centro de la pantalla, no la que apenas asoma por el borde.
     El número de cada marca se pinta desde CSS con un contador, para que
     añadir o quitar una sección no obligue a renumerar el marcado a mano. */
  const railItems = [...document.querySelectorAll('.rail__item')];
  if (railItems.length) {
    const targets = railItems
      .map((a) => ({ a, el: document.getElementById(a.dataset.rail) }))
      .filter((t) => t.el);
  
    const setActive = (id) =>
      targets.forEach(({ a }) => a.setAttribute('aria-current', String(a.dataset.rail === id)));
  
    const seen = new Map();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => seen.set(e.target.id, e.intersectionRatio));
        // gana la sección con más presencia en la banda central
        let best = null, bestRatio = 0;
        for (const { el } of targets) {
          const r = seen.get(el.id) || 0;
          if (r > bestRatio) { bestRatio = r; best = el.id; }
        }
        if (best) setActive(best);
      },
      { rootMargin: '-35% 0px -35% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    targets.forEach(({ el }) => io.observe(el));
    setActive('top');
  }
  
  /* --------------------------------------------------------- revelado ------
     Marca el documento y deja que el CSS oculte lo que va a entrar. Tres
     salvaguardas para que el contenido NUNCA se publique en blanco:
       1. Si no hay IntersectionObserver, no se oculta nada.
       2. Si el usuario pidió menos movimiento, no se oculta nada.
       3. Un temporizador revela todo pase lo que pase, por si la pestaña está
          en segundo plano o el render está pausado — que es justo donde las
          transiciones no corren y el reveal nunca se dispararía. */
  (() => {
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) return;
  
    const items = [...document.querySelectorAll('[data-reveal]')];
    if (!items.length) return;
  
    document.documentElement.classList.add('js-motion');
    const revealAll = () => items.forEach((el) => el.classList.add('is-in'));
  
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      }),
      { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
    );
    items.forEach((el) => io.observe(el));
  
    // red de seguridad
    setTimeout(revealAll, 1400);
    addEventListener('pagehide', revealAll);
  })();
  
  
  /* El encabezado sobre las secciones oscuras.
   *
   * Es fijo y translúcido sobre papel, que funciona mientras la página es clara.
   * Con la secuencia ahora en oscuro y ocupando toda la pantalla, esa barra clara
   * se queda encima un buen rato y parte la sección en dos. Se detecta qué hay
   * justo debajo y se invierte.
   *
   * Con IntersectionObserver no basta: hace falta saber si la sección oscura cruza
   * la franja del encabezado, no si está a la vista. Un rootMargin negativo del
   * alto del encabezado lo resuelve sin escuchar el scroll. */
  
  function headerOverDark() {
    const header = document.querySelector('.header');
    const oscuras = document.querySelectorAll('.on-dark, scroll-sequence');
    if (!header || !oscuras.length) return;
  
    const alto = header.offsetHeight;
    const dentro = new Set();
    const io = new IntersectionObserver((entradas) => {
      for (const e of entradas) {
        e.isIntersecting ? dentro.add(e.target) : dentro.delete(e.target);
      }
      header.classList.toggle('header--on-dark', dentro.size > 0);
    }, { rootMargin: `0px 0px -${innerHeight - alto}px 0px` });
  
    oscuras.forEach((s) => io.observe(s));
  }
  
  
  /* Un video en bucle en la primera pantalla es justo lo que molesta a quien tiene
   * activado «reducir movimiento»: no se puede ignorar, está arriba y no para. El
   * atributo autoplay no se apaga desde CSS, así que se para aquí y se deja el
   * póster, que cuenta lo mismo sin moverse. */
  
  function respetarMovimiento() {
    const mq = matchMedia('(prefers-reduced-motion: reduce)');
    const video = document.querySelector('.hero__video');
    if (!video) return;
    const aplicar = () => {
      if (mq.matches) { video.pause(); video.removeAttribute('autoplay'); }
      else { video.play?.().catch(() => {}); }
    };
    aplicar();
    mq.addEventListener('change', aplicar);
  }

  headerOverDark();
  respetarMovimiento();
}

initCromo();
document.addEventListener('shopify:section:load', initCromo);

/* ------------------------------------------------- menú de la cabecera --- */
/**
 * Por debajo de 750 px la cabecera se quedaba con el logotipo y nada más: 72 px
 * pegajosos sin una sola forma de navegar. El botón abre los mismos enlaces que
 * hay en escritorio, así que no hay dos listas que mantener.
 *
 * El estado vive en un atributo del <header> y el CSS decide: si este script no
 * llega a cargar, la navegación sigue en el marcado y accesible por lector de
 * pantalla, solo que siempre desplegada. Falla hacia el lado bueno.
 */
function initMenu() {
  const boton = document.querySelector('#sib-burger');
  const header = boton && boton.closest('.header');
  if (!boton || !header) return;

  const pon = (abierto) => {
    header.dataset.nav = abierto ? 'open' : 'closed';
    boton.setAttribute('aria-expanded', String(abierto));
  };
  const abierto = () => header.dataset.nav === 'open';

  pon(false);
  boton.addEventListener('click', () => pon(!abierto()));

  // Al elegir destino se cierra: si no, el panel tapa el ancla a la que acaba
  // de saltar.
  header.querySelectorAll('.header__nav a').forEach((a) =>
    a.addEventListener('click', () => pon(false))
  );

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && abierto()) { pon(false); boton.focus(); }
  });

  document.addEventListener('click', (e) => {
    if (abierto() && !header.contains(e.target)) pon(false);
  });

  // Al volver a escritorio el panel deja de existir como tal; el atributo se
  // limpia para que el icono no se quede en aspa.
  matchMedia('(min-width: 750px)').addEventListener('change', (e) => {
    if (e.matches) pon(false);
  });
}

initMenu();
document.addEventListener('shopify:section:load', initMenu);

/* ------------------------------------------------- país y moneda --------- */
/**
 * El selector envía su formulario al cambiar. Sin esto habría que pulsar un
 * botón que nadie busca, y con JS desactivado el <noscript> del marcado ya pone
 * ese botón: el control funciona en los dos casos.
 */
function initLocale() {
  document.querySelectorAll('[data-locale-auto]').forEach((sel) => {
    if (sel.dataset.enganchado) return;
    sel.dataset.enganchado = '1';
    sel.addEventListener('change', () => sel.form && sel.form.submit());
  });
}

initLocale();
document.addEventListener('shopify:section:load', initLocale);
